import moment, {Moment, unitOfTime} from 'moment';
// which can fail type checking if the date util mixes these with moment-timezone instead
import momentTimezone from 'moment-timezone';
import mapValues from 'lodash/mapValues';
import round from 'lodash/round';
import values from 'lodash/values';
import isString from 'lodash/isString';

import {SpecialTimeUnit, StandardTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DAYS_OF_THE_WEEK, TIMEZONES} from 'common/constants';

import {pluralize} from './string';

const MS_IN_MINUTE = 60 * 1000;
const MS_IN_HOUR = MS_IN_MINUTE * 60;
const MS_IN_DAY = MS_IN_HOUR * 24;
export const MS_BY_UNIT = {
  second: 1000,
  minute: MS_IN_MINUTE,
  hour: MS_IN_HOUR,
  day: MS_IN_DAY,
  week: MS_IN_DAY * 7,
  month: MS_IN_DAY * 30,
  quarter: MS_IN_DAY * 90,
  year: MS_IN_DAY * 365,
};

const enLocalData = {
  relativeTime: {
    future: `in %s`,
    past: `%s ago`,
    s: `less than a minute`,
    ss: `%d seconds`,
    m: `1 minute`,
    mm: `%d minutes`,
    h: `1 hour`,
    hh: `%d hours`,
    d: `1 day`,
    dd: `%d days`,
    M: `1 month`,
    MM: `%d months`,
    y: `1 year`,
    yy: `%d years`,
  },
};

/**
 * Setup moment's relative time locales
 */
moment.updateLocale(`en`, enLocalData);
// moment-timezone has its own static global
momentTimezone.updateLocale(`en`, enLocalData);

/**
 * Formats the label for a bucket describing start and end time
 * @param {number} from - start time of bucket in seconds
 * @param {number} to - end time of bucket in seconds
 * @param {number} precision - number of decimal points to round to
 * @return {string|null} formatted string describing start time and end time of bucket
 *
 * @example formatTime(4 * 60, 5 * 60) returns "4-5 minutes"
 */
export function formatTime(from = undefined, to = undefined, precision = 1) {
  if (from === undefined || to === undefined) {
    console.error(`from time ${from} or to time ${to} is not valid`);
    return null;
  }
  return formatInterval(from * 1000, to * 1000, precision);
}

export function getTimeUnit(duration, precision = 1) {
  const {unit} = formatDuration(duration * 1000, precision);
  return unit;
}

export const SECONDS_BY_UNIT = mapValues(MS_BY_UNIT, (val) => val / 1000);

export const UNITS = {
  second: `second`,
  minute: `minute`,
  hour: `hour`,
  day: `day`,
  week: `week`,
  month: `month`,
  quarter: `quarter`,
  year: `year`,
};

const UNITS_ARRAY = [
  UNITS.second,
  UNITS.minute,
  UNITS.hour,
  UNITS.day,
  UNITS.week,
  UNITS.month,
  UNITS.quarter,
  UNITS.year,
];

export const PARSE_DATE_FORMATS = [
  `M D YY`,
  `M D YYYY`,
  `YY M D`,
  `YYYY M D`,
  `YYYY M DTHH mm ss`,
  `MMM D YY`,
  `MMM Do YY`,
  `MMM D YYYY`,
  `MMM Do YYYY`,
  `MMM D YYYY h mmA`,
  `M D`,
  `M YY`,
  `M YYYY`,
  `MMM D`,
  `MMM Do`,
  `MMM YY`,
  `MMMM YY`,
  `MMM YYYY`,
  `MMMM YYYY`,
  `MMMM D YYYY`,
  `MMMM Do YYYY`,
  `MMMM D YY`,
  `MMMM Do YY`,
  `MMMM D`,
  `MMMM Do`,
  // Insights api related formats
  // Parse hour format (e.g. "12pm Tue Sep 19")
  `ha ddd MMM D`,
  `ha MMM D YYYY`,
  `ha MMM D`,
  `h mma MMM D YYYY`,
  `MMM D YYYY H mm a`,
  // Parse week range (e.g. "Jan 02 Jan 08")
  `MMM D MMM D`,
  `MMM D MMM D YYYY`,
];

export const DISPLAY_DATE_FORMATS = {
  [UNITS.minute]: `ddd MMM D, YYYY h:mmA`,
  [UNITS.hour]: `ddd MMM D, YYYY h:mmA`,
  [UNITS.day]: `ddd MMM D, YYYY`,
  [UNITS.week]: `MMM D, YYYY`,
  [UNITS.month]: `MMM YYYY`,
  [UNITS.quarter]: `[Q]Q YYYY`,
  [UNITS.year]: `YYYY`,
};

// region Helpers for mocking dates for testing purposes

export function getCurrentDate() {
  return new Date();
}

export function getCurrentDateAsOf(projectAsOfDate: string) {
  return parseDate(projectAsOfDate) || new Date();
}

type GetCurrentMomentArgType = {
  localized?: boolean;
  timezone?: typeof TIMEZONES[number] | ``;
};

export function getCurrentMoment({localized = false, timezone = ``}: GetCurrentMomentArgType = {}) {
  if (timezone) {
    return momentTimezone.tz(timezone);
  } else {
    return localized ? moment(getCurrentDate()) : moment.utc(getCurrentDate());
  }
}

export function getCurrentMomentAsOf(projectAsOfDate: string) {
  return moment(getCurrentDateAsOf(projectAsOfDate));
}

// endregion

/**
 * Finds the minimum precision (number of decimal places to round to)
 * required to differentiate between a time t represented in
 * a given time measurement unit and another time (t + difference) also
 * expressed in that same unit.
 * @param {string} unit - time unit (second | minute | hour | day | week | month | quarter | year)
 * @param {number} difference - minimum duration in milliseconds between two diffentiable times
 * @return minimum precision required to differentiate between
 *
 * @example
 *   Let time1 be 172800000 ms and time2 be 172800000 + 100000 ms.
 *   When the precision is 2, time1 is equivalent to 2.00 days,
 *   and time2 is also equivalent to 2.00 days,
 *   thus we are unable to distinguish the two different times.
 *   Calling this function getPrecision(`day`, 100000) returns 3.
 *   When the precision is 3, time1 is equivalent to 2.000 days,
 *   and time2 is equivalent to 2.001 days,
 *   thus we are able to use this function to distinguish between the two times.
 */
export function getPrecision(unit, difference) {
  if (!values(UNITS).includes(unit)) throw new Error(`unit must be one of second|minute|hour|day|month|quarter|year`);
  if (difference <= 0) throw new Error(`difference must be a positive number`);

  const msInUnit = MS_BY_UNIT[unit];
  return Math.ceil(Math.log10(msInUnit / difference));
}

function shortenUnit(unit) {
  return unit !== `` ? unit[0] : unit;
}

/**
 * Takes in a time duration returns an approximate human readable dict.
 * Use @see {@link formatPreciseDuration} to obtain the precise duration.
 * @param duration {number} - time duration in milliseconds
 * @param precision {number} - number of decimal points to round to
 * @param concise {bool} - if a concise unit must be returned. (`w` vs `weeks`)
 * @return {{unit: string, value: number}} time unit and numeric value of the interval
 *
 * @example formatDuration(120 * 60000) returns {unit: `hour`, value: 2}
 * @example formatDuration(121 * 60000) returns {unit: `hour`, value: 2}
 */
export function formatDuration(duration, precision, concise = false) {
  const numYears = round(duration / MS_BY_UNIT.year, precision);
  const numMonths = round(duration / MS_BY_UNIT.month, precision);
  const numDays = round(duration / MS_BY_UNIT.day, precision);
  const numHours = round(duration / MS_BY_UNIT.hour, precision);
  const numMinutes = round(duration / MS_BY_UNIT.minute, precision);
  const numSeconds = Math.floor(duration / MS_BY_UNIT.second);

  let formattedTime = {unit: ``, value: 0};
  if (duration === 0) {
    formattedTime = {unit: UNITS.second, value: 0};
  } else if (0 <= numSeconds && numSeconds < 60) {
    formattedTime = {unit: UNITS.second, value: numSeconds};
  } else if (1 <= numMinutes && numMinutes < 60) {
    formattedTime = {unit: UNITS.minute, value: numMinutes};
  } else if (1 <= numHours && numHours < 24) {
    formattedTime = {unit: UNITS.hour, value: numHours};
  } else if (1 <= numDays && numDays < 90) {
    formattedTime = {unit: UNITS.day, value: numDays};
  } else if (1 <= numMonths && numMonths < 12) {
    formattedTime = {unit: UNITS.month, value: numMonths};
  } else if (1 <= numYears) {
    formattedTime = {unit: UNITS.year, value: numYears};
  }

  if (concise) {
    formattedTime.unit = shortenUnit(formattedTime.unit);
  }
  return formattedTime;
}

/**
 * Takes in a time duration in milliseconds returns a precise human readable dict.
 * Use @see {@link formatDuration} when an approximation is sufficient.
 * @param duration {number} - time duration in milliseconds
 * @return {{unit: string, value: number}} time unit and numeric value of the interval
 *
 * @example formatPreciseDuration(120 * 60000) returns {unit: `hour`, value: 2}
 * @example formatPreciseDuration(121 * 60000) returns {unit: `minute`, value: 121}
 */
export function formatPreciseDuration(duration) {
  if (duration < MS_BY_UNIT.second) return {unit: ``, value: 0};

  const numYears = Math.floor(duration / MS_BY_UNIT.year);
  const numWeeks = Math.floor(duration / MS_BY_UNIT.week);
  const numDays = Math.floor(duration / MS_BY_UNIT.day);
  const numHours = Math.floor(duration / MS_BY_UNIT.hour);
  const numMinutes = Math.floor(duration / MS_BY_UNIT.minute);
  const numSeconds = Math.floor(duration / MS_BY_UNIT.second);

  let formattedTime = {unit: ``, value: 0};
  if (duration % MS_BY_UNIT.year === 0 && numYears >= 1) {
    formattedTime = {unit: UNITS.year, value: numYears};
  } else if (duration % MS_BY_UNIT.week === 0 && numWeeks >= 1) {
    formattedTime = {unit: UNITS.week, value: numWeeks};
  } else if (duration % MS_BY_UNIT.day === 0 && numDays >= 1) {
    formattedTime = {unit: UNITS.day, value: numDays};
  } else if (duration % MS_BY_UNIT.hour === 0 && numHours >= 1) {
    formattedTime = {unit: UNITS.hour, value: numHours};
  } else if (duration % MS_BY_UNIT.minute === 0 && numMinutes >= 1) {
    formattedTime = {unit: UNITS.minute, value: numMinutes};
  } else if (duration % MS_BY_UNIT.second === 0 && numSeconds >= 1) {
    formattedTime = {unit: UNITS.second, value: numSeconds};
  }
  return formattedTime;
}

/**
 * Formats a single duration in concise format for annotating median.
 * @param duration
 * @return {string} formatted string describing the median
 */
export function formatConciseDuration(duration, precision = 1): string {
  if (duration === undefined) {
    console.error(`duration ${duration} is not valid`);
    return ``;
  }
  const {unit: u, value: v} = formatDuration(duration * 1000, precision, true);
  return `${v}${u}`;
}

/**
 * Takes in a start time in milliseconds and stop time in milliseconds
 * and returns a formatted string denoting the time interval between them
 * @param {number} from - start time in milliseconds
 * @param {number} to - end time in milliseconds
 * @param {number} precision - number of decimal points to round to
 * @return {string} readable time interval between from and to
 *
 * @example
 *   from: 4 * 60 * 1000
 *   to: 5 * 60 * 1000
 *   formatInterval(from, to) returns '4 - 5 minutes'
 */
export function formatInterval(from, to, precision = 1) {
  const {unit: fromUnit, value: fromValue} = formatDuration(from, precision);
  const {unit: toUnit, value: toValue} = formatDuration(to, precision);

  let intervalStr = `--`;
  if (fromUnit === toUnit) {
    if (fromValue === toValue) {
      intervalStr = `${toValue} ${pluralize(toUnit, toValue)}`;
    } else {
      intervalStr = `${fromValue} - ${toValue} ${pluralize(toUnit, toValue)}`;
    }
  } else {
    if (fromValue === 0) {
      intervalStr = `0 - ${toValue} ${pluralize(toUnit, toValue)}`;
    } else {
      intervalStr = `${fromValue} ${pluralize(fromUnit, fromValue)} - ${toValue} ${pluralize(toUnit, toValue)}`;
    }
  }
  return intervalStr;
}

function preProcessDateString(dateString: string): string {
  // recent versions of moment return moment.invalid when day of week doesn't match date
  // (e.g. "Tue Dec 1" in 2018 when Dec 1 is Sat). In order to preserve current Insights
  // behavior (from moment 2.17.1) we strip out day of week (matching format string "ddd").
  dateString = dateString.replace(/(mon|tue|wed|thu|fri|sat|sun),?/gi, ``).trim();
  // replace all chars other than alphanumerics and spaces with a space
  // and replace multiple spaces with a single space
  // this allows strings like '"2017-02-02"' to be parsed correctly in strict mode
  dateString = dateString.replace(/\W+/g, ` `);
  return dateString;
}

function parseToMostRecentPast(momentDate: Moment): Nullable<Moment> {
  momentDate =
    momentDate > moment().endOf(UNITS.day as unitOfTime.StartOf)
      ? momentDate.subtract(1, `${UNITS.year}s` as unitOfTime.DurationConstructor)
      : momentDate;
  return momentDate;
}

function formatWeekRange(mDate) {
  const mToDate = moment(mDate).add(6, `days`);
  const from = mDate.format(mDate.year() !== mToDate.year() ? `MMM D, YYYY` : `MMM D`);
  const to = mToDate.format(`MMM D, YYYY`);
  /** Dec 5 - Dec 11 2021 */
  return `${from} - ${to}`;
}

export type ParseDateOptions = Partial<{
  /** treat input date string as an ISO-formatted UTC date (i.e. "2017-02-28") */
  iso: boolean;
  /** set date time to start of day */
  startOfDay: boolean;
  /** set date time to end of day */
  endOfDay: boolean;
}>;
/**
 * Parse a date string into a Date object (sets date time to start of day)
 * Strings that parse to a future date (i.e. "12/31") are moved to the most recent *past* instance of that date
 * @param {string} dateString - string to parse
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.iso=false] - treat input date string as an ISO-formatted UTC date (i.e. "2017-02-28")
 * @param {boolean} [config.startOfDay=false] - set date time to start of day
 * @param {boolean} [config.endOfDay=false] - set date time to end of day
 * @returns {?Date} - the Date object the input string parses to, or null if input is invalid
 */
export function parseDate(
  dateString: string,
  {iso = false, startOfDay = false, endOfDay = false}: ParseDateOptions = {},
): Nullable<Date> {
  let date: Nullable<Date> = null;
  let mDate;

  if (typeof dateString === `string`) {
    if (iso) {
      mDate = moment.utc(new Date(dateString));
    } else {
      dateString = preProcessDateString(dateString);
      mDate = moment(dateString, PARSE_DATE_FORMATS, /* strict */ true);
    }

    if (mDate.isValid()) {
      mDate = startOfDay ? mDate.startOf(UNITS.day) : mDate;
      mDate = endOfDay ? mDate.endOf(UNITS.day) : mDate;

      // make dates like "12/31" parse to the most recent past instance of that date
      // use "strict parsing" mode of moment to check whether dateString is of the form M/D, MM/DD, M.D, etc.
      if (moment(dateString, `M D`, true).isValid()) {
        mDate = parseToMostRecentPast(mDate);
      }

      date = new Date(mDate);
    }
  }

  return date;
}

/**
 * Parses dateString which is in one of the formats PARSE_DATE_FORMATS. It is important to note that
 * dateString MUST be parsed with a correct timezone. For example, 2022-10-01T00:00:00 America/Los_Angeles is different
 * from 2022-10-01T00:00:00 Australia/Melbourne. In order to parse a given string in a specified timezone, we use MomentTimezone
 * We use strict parsing mode for performance reasons.
 * Strings that parse to a future date (i.e. "12/31") are moved to the most recent *past* instance of that date
 * @param {string} dateString - string to parse
 * @param {typeof TIMEZONES[number]} timezone  - timezone to be used when parsing dateString
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.startOfDay=false] - set date time to start of day
 * @param {boolean} [config.endOfDay=false] - set date time to end of day
 * @returns {?Moment} - timezone aware Moment object the input string parses to, or null if input is invalid
 */
export function parseDateWithTimeZone(
  dateString: string,
  timezone: typeof TIMEZONES[number],
  {startOfDay = false, endOfDay = false}: ParseDateOptions = {},
): Nullable<Moment> {
  let mDate;

  if (typeof dateString === `string`) {
    dateString = preProcessDateString(dateString);
    mDate = momentTimezone.tz(dateString, PARSE_DATE_FORMATS, /* strict */ true, timezone);
  }

  if (mDate.isValid()) {
    mDate = startOfDay ? mDate.startOf(UNITS.day) : mDate;
    mDate = endOfDay ? mDate.endOf(UNITS.day) : mDate;

    // make dates like "12/31" parse to the most recent past instance of that date
    // use "strict parsing" mode of moment to check whether dateString is of the form M/D, MM/DD, M.D, etc.
    if (moment(dateString, `M D`, true).isValid()) {
      mDate = parseToMostRecentPast(mDate);
    }
  }
  return mDate;
}

type FormatDateOptions = Partial<{
  iso: boolean;
  utc: boolean;
  unit: Nullable<string>;
  customFormatting: Record<string, string>;
  displayRangeIfWeek: boolean;
  weekRangeFormatter: (mDate: Moment) => string;
}>;
/**
 * Convert a Date object or integer timestamp into a formatted date string
 * @param {Date|number} date - Date object or timestamp to format
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.iso=false] - format date to ISO-style string (YYYY-MM-DD)
 * @param {boolean} [config.utc=false] - treat input as a UTC date
 * @param {string|null} [config.unit=null] - use the corresponding format for this unit from DISPLAY_DATE_FORMATS
 * @param {Object} [config.customFormatting={}] - map of units to custom format strings to be used in place of DISPLAY_DATE_FORMATS
 * @param {boolean} [config.displayRangeIfWeek=true] - if unit is week format to range string across week (i.e. "Dec 1 - Dec 7")
 * @param {Function} [config.weekRangeFormatter] - function for formatting weekly ranges
 * @returns {?string} - the string the input Date object formats to, or null if input is invalid
 */
export function formatDate(
  date,
  {
    iso = false,
    utc = false,
    unit = null,
    customFormatting = {},
    displayRangeIfWeek = true,
    weekRangeFormatter = formatWeekRange,
  }: FormatDateOptions = {},
): Nullable<string> {
  let dateString: Nullable<string> = null;
  let mDate: Moment;

  if (date instanceof Date || Number.isInteger(date)) {
    mDate = utc ? moment.utc(Number(date)) : moment(Number(date));

    if (mDate.isValid()) {
      if (iso) {
        dateString = mDate.format().slice(0, 10);
      } else {
        if (displayRangeIfWeek && unit === UNITS.week) {
          dateString = weekRangeFormatter(mDate);
        } else {
          let format: Nullable<string> = null;
          if (unit) {
            format = customFormatting[unit] || DISPLAY_DATE_FORMATS[unit];
          }
          dateString = mDate.format(format || `MMM D, YYYY`);
        }
      }
    }
  }

  return dateString;
}

type LocalizedDateOptions = Partial<{utcOffset: Nullable<number>; date: Nullable<Date>}>;
/**
 * Returns the date provided in project localized time. If no date specificed use now.
 * This was largely taken from analytics/media/js/utility.js
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - config.the offset of UTC time in hours
 * @param {Date|null} [config.date=null] - a Date object to be offset. if this is not provided the function will create a new Date
 * @returns {Date} - a Date in "project time" according to the utcOffset provided
 */
export function localizedDate({utcOffset = 0, date = null}: LocalizedDateOptions = {}): Date {
  date = date instanceof Date ? date : new Date();
  date = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
  if (utcOffset && Number.isInteger(utcOffset)) {
    date.setMinutes(date.getMinutes() + utcOffset);
  }
  return date;
}

/**
 * Convert a "relative date" integer (i.e. 5 [days ago]) and a unit into an "absolute" Date object
 * @param {number} relativeDateInt - integer that (along with unit) represnts a time offfset from the current date
 * @param {string} unit - specifies the unit of time to multiply the relativeDateInt by to calculate the absolute date
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - the offset of UTC time in hours
 * @returns {?Date} - the Date object the given relative date integer and unit resolve to, or null if input is invalid
 */
export function relativeToAbsoluteDate(relativeDateInt: number, unit: string, {utcOffset = null} = {}): Nullable<Date> {
  let date: Nullable<Date> = null;
  const now = utcOffset === null ? moment() : moment(localizedDate({utcOffset}));

  if (Number.isInteger(relativeDateInt) && UNITS_ARRAY.includes(unit)) {
    date = now.subtract(relativeDateInt, `${unit}s` as unitOfTime.DurationConstructor).toDate();
  }

  return date;
}

export function transformDateByRelativeDate(date, {unit, value}: {unit: StandardTimeUnit; value: number}) {
  return moment(date).add(value, unit).toDate();
}

/**
 * Accepts a (from, to) date range and the focused date ('to' or 'from') being edited. Normalizes the date range
 * if from > to:
 * 1) if focusedDate is `from`, sets to = from
 * 2) if focusedDate is `to`, sets from = to
 * @param {string} from - from date
 * @param {string} to - to date
 * @param {string} focusedDate - the date being edited resulting in the current date range; one of <`from`, `to`>
 * @returns {Array.<string>} - the normalized date range [from, to]
 */
export function normalizeDateRange(from: string, to: string, focusedDate: 'from' | 'to'): [string, string] {
  if (!from || !to) {
    return [from, to];
  }

  const parsedFrom = parseDate(from) ?? 0;
  const parsedTo = parseDate(to) ?? 0;

  if (focusedDate === `from`) {
    if (parsedFrom > parsedTo) {
      return [from, from];
    }
  } else if (focusedDate === `to` && parsedTo < parsedFrom) {
    return [to, to];
  }

  return [from, to];
}

/**
 * Accepts a list of date strings and
 * - ensures none of them exceed the current moment (unless allowFuture=true is specified)
 * - sorts them in ascending order
 * - formats them as ISO date strings (YYYY-MM-DD)
 * @param {Array.<string>} dates - list of date strings to normalize, can be any format that parseDate accepts
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - the offset of UTC time in hours
 * @param {boolean} [config.allowFuture=false] - if true, does not normalize future dates to the current moment
 * @returns {Array.<?string>} - list of ISO-formatted normalized date strings, in ascending order
 */
export function normalizeDateStrings(dateStrings: string[] = [], {utcOffset = null, allowFuture = false} = {}) {
  let dates = dateStrings.reduce((dates, date) => {
    const parsed = parseDate(date);
    if (parsed) {
      dates.push(parsed);
    }
    return dates;
  }, [] as Date[]);

  if (!allowFuture) {
    const now = utcOffset === null ? new Date() : localizedDate({utcOffset});
    dates = dates.map((date) => (date > now ? now : date));
  }

  return dates
    .map((date) => date.getTime())
    .sort((a, b) => a - b)
    .map((date) => formatDate(date, {iso: true}));
}

/**
 * Returns a unit string corresponding with the time delta between two Dates
 * @param {Date|number} from - the starting date of the range - can be Date or timestamp
 * @param {Date|number} to - the ending date of the range - can be Date or timestamp
 * @returns {?string} - one of DATE_UNITS that the input range corresponds with, or null if the input was invalid
 */
export function getDefaultTrendUnit(daysApart: number): Nullable<string> {
  if (daysApart <= 4) {
    return UNITS.hour;
  } else if (daysApart <= 31) {
    return UNITS.day;
  } else if (daysApart <= 183) {
    return UNITS.week;
  } else {
    return UNITS.month;
  }
}

// if DST is active on given date, will generally return 60 (minutes),
// though there are timezones that use 30 minute DST offset (e.g. Australia/Lord_Howe)
export function getDstOffset(momentDate) {
  const nonDstOffset = momentTimezone(momentDate).month(0).utcOffset(); // use a month in the winter (no DST)
  return momentDate.utcOffset() - nonDstOffset; // amount momentDate is offset due to DST
}

// offset times by the diff between project and local timezone
// to trick Highcharts into displaying data in project time
export function offsetForHighcharts(momentDate, projectTimezone, projectOffset) {
  // Correct for DST - projectOffset changes depending on whether DST is active at the current moment in project time.
  // This doesn't work for us since local time may have different DST rules, so correct the offsets accordingly.
  projectOffset = projectOffset - getDstOffset(momentTimezone.tz(projectTimezone));
  const localOffset = momentDate.utcOffset() - getDstOffset(momentTimezone.tz(momentDate, projectTimezone));
  return moment(momentDate).add(projectOffset - localOffset, `minutes`);
}

/**
 * Uses moment-timezone to convert a potentially tz-unaware datetime string that
 * we know is in UTC to browser timezone.
 *
 * We use momentTimezone specifically so we can use the 'z' flag when formatting
 * See: https://momentjs.com/docs/#/displaying/format/
 *
 * @param {string} ds
 * @returns {momentTimezone.Moment} moment object
 */
export function utcToLocal(ds) {
  const {tz, utc} = momentTimezone;
  return tz(utc(ds), tz.guess());
}

/**
 * Non moment util to convert datetime string in UTC to browser timezone
 */
export function convertUTCToLocalized(ds) {
  return new Date(ds);
}

/** Modifies a moment object to that was created in the local timezone context (e.g. moment.now() )
 * and converts it to a project relevant time
 * E.g. If we're noon PST (-7) and the project timezone is europe (+2), we'll modify the moment to remove the -7 and add the +2
 * @param {Moment} time
 * @param {number} utcOffset - the project timezone
 * @returns {Moment} the original moment, modified
 */
export function localizedMomentToProjectMoment(time, utcOffset) {
  time.add(new Date().getTimezoneOffset(), `minutes`); // remove current browser timezone (local -> utc)
  time.add(utcOffset, `minutes`); // add project offset (utc -> project)

  return time;
}

// TODO: This might not work properly across DST savings boundaries. Need to verify.
/** Sort a list of ISO date strings
 * @param {Array.<string>} isoDates - list of iso dates
 * @returns {Array.<string>} sorted dates
 */
export function sortIsoDates(isoDates: string[]): string[] {
  return isoDates.sort();
}

/**
 * Returns the ISO 8601 calendar date from a date
 *
 * @param {string|Date|moment|Moment} date
 * @param {boolean} granular
 */
export function getCalendarDate(date, granular = false) {
  if (granular) {
    return moment.parseZone(date).format(`YYYY-MM-DDTHH:mm:ss`);
  }
  return moment.parseZone(date).format(`YYYY-MM-DD`);
}

function getMonthStartDate(month, year) {
  return getCalendarDate(new Date(year, month - 1, 1));
}

function getMonthEndDate(month, year) {
  return getCalendarDate(new Date(year, month, 0));
}

function parseMinute(dateStr) {
  const parsedDate = parseDate(dateStr);
  if (parsedDate) {
    const fromDate = moment(parsedDate).format(`YYYY-MM-DDTHH:mm:ss`);
    const toDate = moment(fromDate).add(1, `minute`);
    return [fromDate, toDate.format(`YYYY-MM-DDTHH:mm:ss`)];
  }
  return null;
}

function parseHour(dateStr) {
  const parsedDate = parseDate(dateStr);
  if (parsedDate) {
    const fromDate = moment(parsedDate).format(`YYYY-MM-DDTHH:mm:ss`);
    const toDate = moment(fromDate).add(1, `hour`);
    return [fromDate, toDate.format(`YYYY-MM-DDTHH:mm:ss`)];
  }
  return null;
}

function parseDay(day) {
  try {
    /** parses days with the following format: Oct 07 '20 */
    return getCalendarDate(new Date(isString(day) ? day.replace(`'`, ``) : day));
  } catch (err) {
    return null;
  }
}

function parseWeek(dateStr) {
  if (dateStr.indexOf(` - `) !== -1) {
    const toDateStr = dateStr.split(` - `)[1];
    const toDate = getCalendarDate(toDateStr);
    const fromDate = moment(toDate).subtract(6, `day`);
    if (fromDate && toDate) {
      return [getCalendarDate(fromDate), toDate];
    }
  }
  return null;
}

function parseMonth(dateStr) {
  const months = [`jan`, `feb`, `mar`, `apr`, `may`, `jun`, `jul`, `aug`, `sep`, `oct`, `nov`, `dec`];

  const monthRegex = new RegExp(`^[a-zA-z]{3} \\d{4}`);
  if (monthRegex.test(dateStr)) {
    const [monthStr, yearStr] = dateStr.split(` `);
    const monthIdx = months.indexOf(monthStr.toLowerCase());
    const yearNum = Number(yearStr);

    if (monthIdx !== -1 && !Number.isNaN(yearNum)) {
      return [getMonthStartDate(monthIdx + 1, yearNum), getMonthEndDate(monthIdx + 1, yearNum)];
    }
  }

  return null;
}

function parseQuarter(dateStr, quarterStartMonth) {
  const quarterRegex = new RegExp(`^Q[1-4] \\d{4}`);
  if (quarterRegex.test(dateStr)) {
    const [quarterStr, yearStr] = dateStr.split(` `);
    const quarterNum = Number(quarterStr[1]);
    const startMonth = (quarterNum - 1) * 3 + 1 + (quarterStartMonth % 3);
    let endMonth = quarterNum * 3 + (quarterStartMonth % 3);
    let yearNum = Number(yearStr);

    const startDate = getMonthStartDate(startMonth, yearNum);
    if (endMonth > 12) {
      endMonth -= 12;
      yearNum++;
    }
    const endDate = getMonthEndDate(endMonth, yearNum);

    return [startDate, endDate];
  }
  return null;
}

function parseYear(dateStr) {
  const yearRegex = new RegExp(`^\\d{4}$`);
  if (yearRegex.test(dateStr)) {
    return [dateStr.concat(`-01-01`), dateStr.concat(`-12-31`)];
  }
  return null;
}

/**
 * Parses the data and returns a date range [from, to]
 *
 * @param {string} date - date to parse
 * @param {SpecialTimeUnit} unit - time unit to format the date
 * @returns {?string} - a stringified index that maps to special date
 */
export function parseSpecialDate(date: string, unit: SpecialTimeUnit) {
  if (date === `undefined`) return date;

  switch (unit) {
    case SpecialTimeUnit.HourOfDay:
      return String(Number(date.split(`:`)[0]));
    case SpecialTimeUnit.DayOfWeek:
      return String(DAYS_OF_THE_WEEK.findIndex((day) => day === date));
    default:
      return null;
  }
}

/**
 * Parses the data and returns a date range [from, to]
 *
 * @param {Date|number|string} date - date to parse
 * @param {TimeUnit} unit - time unit to format the date
 * @param {number} number - quarter start month
 * @returns {Array.<string>} - date range [from, to]
 */
export function parseDatetimeRange(date: Date | number | string, unit: TimeUnit, quarterStartMonth?: number) {
  const formattedDate = isString(date) ? date : formatDate(date, {unit});
  if (!formattedDate) return null;
  if (formattedDate === `undefined`) return date;

  switch (unit) {
    case TimeUnit.Minute:
      return parseMinute(formattedDate);
    case TimeUnit.Hour:
      return parseHour(formattedDate);
    case TimeUnit.Day:
      return parseDay(formattedDate);
    case TimeUnit.Week:
      return parseWeek(formattedDate);
    case TimeUnit.Month:
      return parseMonth(formattedDate);
    case TimeUnit.Quarter: {
      return parseQuarter(formattedDate, quarterStartMonth);
    }
    case TimeUnit.Year:
      return parseYear(formattedDate);
    default:
      return null;
  }
}

export function assertIsStandardTimeUnit(unit: TimeUnit): asserts unit is StandardTimeUnit {
  if (unit === TimeUnit.Session) {
    throw new Error(`${unit} is not a StandardTimeUnit`);
  }
}

export function dayDifferenceBetweenDates(laterDate: string, earlierDate: string) {
  const mLaterDate = moment(laterDate, `YYYY-MM-DD`);
  const mEarlierDate = moment(earlierDate, `YYYY-MM-DD`);
  return mLaterDate.diff(mEarlierDate, `days`);
}
